import { TreeItem, TreeConfig, DropdownOption } from "./types";

/**
 * Response extraction strategies for different API response structures
 */
const RESPONSE_EXTRACTORS: Array<{
  match: (response: any, fieldName: string) => boolean;
  extract: (response: any, fieldName: string) => { items: TreeItem[]; parentComponentData: any | null };
}> = [
  {
    // Case 1: response is { parentComponent: {...}, childItems: [...] }
    match: (response, fieldName) => Array.isArray(response[fieldName]),
    extract: (response, fieldName) => ({
      items: response[fieldName],
      parentComponentData: response,
    }),
  },
  {
    // Case 2: response is wrapped in data: { data: { parentComponent: {...}, childItems: [...] } }
    match: (response, fieldName) => response.data && Array.isArray(response.data[fieldName]),
    extract: (response, fieldName) => ({
      items: response.data[fieldName],
      parentComponentData: response.data,
    }),
  },
  {
    // Case 3: response.data is the array directly: { data: [...] }
    match: (response) => Array.isArray(response.data),
    extract: (response) => ({
      items: response.data,
      parentComponentData: null,
    }),
  },
  {
    // Case 4: response is the array directly
    match: (response) => Array.isArray(response),
    extract: (response) => ({
      items: response,
      parentComponentData: null,
    }),
  },
];

const DEFAULT_EXTRACTION_RESULT = { items: [] as TreeItem[], parentComponentData: null };

/**
 * Extracts child items from various response structures
 */
export const extractChildItemsFromResponse = (
  response: any,
  childItemsFieldName: string = "items"
): { items: TreeItem[]; parentComponentData: any | null } => {
  if (!response) return DEFAULT_EXTRACTION_RESULT;

  const extractor = RESPONSE_EXTRACTORS.find((e) => e.match(response, childItemsFieldName));
  return extractor?.extract(response, childItemsFieldName) ?? DEFAULT_EXTRACTION_RESULT;
};

/**
 * Finds all item IDs that have children (for auto-expand)
 */
export const findItemsWithChildren = (
  items: TreeItem[],
  treeConfig: TreeConfig
): Set<string> => {
  const result = new Set<string>();
  const childrenField = treeConfig.itemDisplayFields?.childrenField || "children";
  const idField = treeConfig.fieldMappings?.id || "id";

  const traverse = (treeItems: TreeItem[]) => {
    if (!Array.isArray(treeItems)) return;

    treeItems.forEach((item) => {
      const children = item[childrenField];
      if (children && Array.isArray(children) && children.length > 0) {
        result.add(String(item[idField]));
        traverse(children);
      }
    });
  };

  traverse(items);
  return result;
};

/**
 * Flattens a tree structure into a flat array
 */
export const flattenTree = (
  items: TreeItem[],
  childrenField: string = "children"
): TreeItem[] => {
  if (!Array.isArray(items)) return [];

  const result: TreeItem[] = [];
  items.forEach((item) => {
    result.push(item);
    if (item[childrenField] && Array.isArray(item[childrenField])) {
      result.push(...flattenTree(item[childrenField], childrenField));
    }
  });
  return result;
};

/**
 * Builds dropdown options for parent selection
 */
export const buildParentDropdownOptions = (
  items: TreeItem[],
  treeConfig: TreeConfig,
  editingItemId: string | null
): DropdownOption[] => {
  const childrenField = treeConfig.itemDisplayFields?.childrenField || "children";
  const idField = treeConfig.fieldMappings.id;
  const parentIdField = treeConfig.fieldMappings.parentId;
  const orderField = treeConfig.fieldMappings.orderIndex || "order_index";
  const titleField = treeConfig.fieldMappings.title || "title";

  const flatItems = flattenTree(items, childrenField);

  const isDescendant = (checkId: string, ancestorId: string): boolean => {
    const foundItem = flatItems.find((i) => String(i[idField]) === checkId);
    if (!foundItem) return false;

    const parentId = foundItem[parentIdField];
    const parentIdStr = parentId ? String(parentId) : null;

    // Check conditions using short-circuit evaluation
    return parentIdStr === ancestorId || (parentIdStr !== null && isDescendant(parentIdStr, ancestorId));
  };

  const buildOptions = (
    allItems: TreeItem[],
    parentId: string | null = null,
    level: number = 0
  ): DropdownOption[] => {
    if (!Array.isArray(allItems)) return [];

    return allItems
      .filter((item) => {
        const itemParentId = item[parentIdField] ? String(item[parentIdField]) : null;
        return itemParentId === parentId;
      })
      .sort((a, b) => (a[orderField] || 0) - (b[orderField] || 0))
      .flatMap((item) => {
        // Exclude current editing item and its descendants
        if (editingItemId) {
          if (
            String(item[idField]) === editingItemId ||
            isDescendant(String(item[idField]), editingItemId)
          ) {
            return [];
          }
        }

        return [
          {
            value: String(item[idField]),
            label: "  ".repeat(level) + (level > 0 ? "└─ " : "") + item[titleField],
          },
          ...buildOptions(allItems, String(item[idField]), level + 1),
        ];
      });
  };

  return [
    {
      value: treeConfig.parentTopLevelValue || "null",
      label: treeConfig.parentTopLevelLabel || "-- Top Level --",
    },
    ...buildOptions(flatItems),
  ];
};

/**
 * Builds default field values from field config
 */
export const buildDefaultFieldValues = (fieldsConfig: any[]): Record<string, any> => {
  return fieldsConfig.reduce((defaults, field) => {
    if (field.defaultValue !== undefined) {
      defaults[field.fieldName] = field.defaultValue;
    }
    return defaults;
  }, {} as Record<string, any>);
};

/**
 * Extracts parent component fields from data based on config
 */
export const extractParentComponentFields = (
  data: Record<string, any>,
  parentFieldsConfig: any[]
): Record<string, any> => {
  const extractedFields: Record<string, any> = {};

  parentFieldsConfig.forEach((field: any) => {
    const fieldName = field.fieldName;
    if (fieldName in data) {
      extractedFields[fieldName] = data[fieldName] ?? field.defaultValue ?? "";
    } else if (field.defaultValue !== undefined) {
      extractedFields[fieldName] = field.defaultValue;
    }
  });

  return extractedFields;
};

/**
 * Gets the field value based on field type and item data
 */
const getFieldValue = (
  item: TreeItem,
  field: any,
  parentIdField: string,
  treeConfig: TreeConfig
): any => {
  const { fieldName, defaultValue } = field;
  const itemValue = item[fieldName];

  // Field not present in item - use default
  if (itemValue === undefined) return defaultValue;

  // Parent ID field - convert to string for dropdown matching
  const isParentField = fieldName === parentIdField;
  if (isParentField) {
    return itemValue != null ? String(itemValue) : (treeConfig.parentTopLevelValue || "null");
  }

  // Regular field - use item value directly
  return itemValue;
};

/**
 * Maps tree item data to form fields
 */
export const mapItemToFormFields = (
  item: TreeItem,
  fieldsConfig: any[],
  treeConfig: TreeConfig
): Record<string, any> => {
  const idField = treeConfig.fieldMappings?.id || "id";
  const parentIdField = treeConfig.fieldMappings?.parentId || "parent_id";

  // Build form data from fields config using reduce
  const formData = fieldsConfig.reduce(
    (acc, field) => ({
      ...acc,
      [field.fieldName]: getFieldValue(item, field, parentIdField, treeConfig),
    }),
    { id: item[idField] } // Include id field so FieldsFormatter can detect change
  );

  return formData;
};

/**
 * Creates a unique temporary ID for new items
 */
export const createTempId = (): string => `new_${Date.now()}`;

/**
 * Checks if an item ID is temporary (not yet saved)
 */
export const isTempId = (id: string | null): boolean => {
  return id !== null && String(id).startsWith("temp_");
};

/**
 * Prepares section data for preview update
 */
export const prepareSectionDataForPreview = (
  selectedSection: any,
  parentComponentId: string | number,
  parentComponentData: any,
  parentComponentFields: Record<string, any>,
  childItems: TreeItem[],
  childItemsFieldName: string,
  pageId?: string,
  pageComponentId?: string
): any => {
  return {
    ...selectedSection,
    componentId: String(parentComponentId),
    data: {
      ...(parentComponentData || {}),
      id: parentComponentData?.id || Number(parentComponentId),
      ...parentComponentFields,
      page_id: pageId ? Number(pageId) : undefined,
      component_id:
        selectedSection?.data?.component_id ||
        (pageComponentId ? Number(pageComponentId) : undefined),
      [childItemsFieldName]: childItems,
    },
  };
};

